import type { IntlKey } from 'locales';
import { useCallback, useMemo } from 'react';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';
import type { WorkspaceType } from 'state/workspaces/model';
import { camelCaseToWords } from 'utils/string';

export type UseFormatLabelOptions = {
  inputId: string;
  label?: IntlKey;
  workspaceType?: WorkspaceType;
};
export const useFormatFormGroupLabel = (workspaceType?: WorkspaceType) => {
  const { formatMessage } = useIntl();

  return useCallback(
    ({ inputId, label }: Omit<UseFormatLabelOptions, 'workspaceType'>) => {
      switch (workspaceType) {
        case 'dynamic':
          return camelCaseToWords(inputId);
        case 'newBatch':
          return formatMessage({
            id: `form.${(label ?? inputId).replace('form.', '')}` as IntlKey,
            defaultMessage: camelCaseToWords(inputId),
          });
        default:
          return label === undefined ? camelCaseToWords(inputId) : formatMessage({ id: label });
      }
    },
    [formatMessage, workspaceType],
  );
};

export const useFormattedFormGroupLabel = ({ inputId, label, workspaceType }: UseFormatLabelOptions) => {
  const { formatMessage } = useIntl();

  return useMemo(
    () => formatFormGroupLabel(workspaceType, inputId, formatMessage, label),
    [formatMessage, inputId, label, workspaceType],
  );
};

function formatFormGroupLabel(
  workspaceType: WorkspaceType | undefined,
  inputId: string,
  formatMessage: IntlShape['formatMessage'],
  label: IntlKey | undefined,
) {
  const defaultMessage = camelCaseToWords(inputId);
  if (workspaceType === 'dynamic' || label === undefined) {
    return defaultMessage;
  } else {
    return formatMessage({ id: label, defaultMessage });
  }
}
